import React, { useEffect, useState } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { Button, Row, Col, Card } from 'react-bootstrap';
import brandLogo from '../assets/images/logo.png';

/**
 * DeepLinkRedirect Component
 * Handles deep linking to Yoospeak app or app stores
 * Replicates functionality from deepLink.hbs template
 * 
 * Supports multiple formats:
 * - /video/:id
 * - /teacher/:id
 * - /student/:id
 * - /community/:id
 * - /live/:id
 * - /deeplink/callback?resourceType=...&id=...
 */
export const DeepLinkRedirect = () => {
  const location = useLocation();
  const params = useParams();
  const [deepLinkData, setDeepLinkData] = useState({
    videoId: '',
    liveId: '',
    communityId: '',
    userId: '',
    userType: '',
    resourceType: 'video',
    title: 'Yoospeak - Learn & Earn',
    description: 'Open in Yoospeak app'
  });

  useEffect(() => {
    // Parse query parameters
    const queryParams = new URLSearchParams(location.search);

    let videoId = queryParams.get('videoId') || '';
    let liveId = queryParams.get('liveId') || '';
    let communityId = queryParams.get('communityId') || '';
    let userId = queryParams.get('userId') || '';
    let userType = queryParams.get('userType') || '';
    let resourceType = queryParams.get('resourceType') || 'video';
    const title = queryParams.get('title') || 'Yoospeak - Learn & Earn';
    const description = queryParams.get('description') || 'Open in Yoospeak app';

    // Extract resource type and ID from path
    if (location.pathname.includes('/teacher/')) {
      resourceType = 'profile';
      userType = 'teacher';
      userId = params.id || '';
    } else if (location.pathname.includes('/student/')) {
      resourceType = 'profile';
      userType = 'student';
      userId = params.id || '';
    } else if (location.pathname.includes('/community/')) {
      resourceType = 'community';
      communityId = params.id || '';
    } else if (location.pathname.includes('/video/')) {
      resourceType = 'video';
      videoId = params.id || '';
    } else if (location.pathname.includes('/live/')) {
      resourceType = 'live';
      liveId = params.id || '';
    }

    setDeepLinkData({
      videoId,
      liveId,
      communityId,
      userId,
      userType,
      resourceType,
      title,
      description
    });
  }, [location, params]);

  const handleOpenApp = () => {
    const { videoId, liveId, communityId, userId, userType, resourceType } = deepLinkData;

    // Detect device type
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isChrome = /Chrome\//i.test(navigator.userAgent) &&
      !/Edg\//i.test(navigator.userAgent) &&
      !/OPR\//i.test(navigator.userAgent);

    // Build deeplink path
    let deepLinkPath = '';
    if (resourceType === 'video' && videoId) {
      deepLinkPath = `video?id=${encodeURIComponent(videoId)}`;
    } else if (resourceType === 'live' && liveId) {
      deepLinkPath = `live?id=${encodeURIComponent(liveId)}`;
    } else if (resourceType === 'community' && communityId) {
      deepLinkPath = `community?id=${encodeURIComponent(communityId)}`;
    } else if (resourceType === 'profile' && userId && userType) {
      deepLinkPath = `${userType}?id=${encodeURIComponent(userId)}`;
    }

    const customScheme = deepLinkPath ? `yoospeak://${deepLinkPath}` : 'yoospeak://';
    const androidIntent = deepLinkPath
      ? `intent://${deepLinkPath}#Intent;scheme=yoospeak;package=com.yoospeak.learning;launchFlags=268435456;end`
      : `intent://#Intent;scheme=yoospeak;package=com.yoospeak.learning;launchFlags=268435456;end`;

    try {
      if (isAndroid && isChrome) {
        window.location.href = androidIntent;
      } else if (isAndroid) {
        window.location.href = customScheme;
      } else if (isIOS) {
        // Use hidden anchor for iOS reliability
        const a = document.createElement('a');
        a.href = customScheme;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.location.href = customScheme;
        }, 300);
      }
    } catch (e) {
      console.log('Failed to open app:', e);
    }
  };

  const stats = [
    {
      icon: 'bi-eye',
      label: 'Videos Views',
      value: '12.5K'
    },
    {
      icon: 'bi-link-45deg',
      label: 'Total Clicks',
      value: '03.5K'
    },
    {
      icon: 'bi-people',
      label: 'Followers',
      value: '08.5K'
    }
  ];

  return (
    <div className="d-flex align-items-center min-vh-100 px-3 py-4" style={{
      background: "linear-gradient( 135deg, #2193F2 0%, #4242F5 49%, #3F13E0 69%, #1E90FC 100% )"
    }}>
      <div className="custom-wrapper text-center mx-auto w-100" style={{ maxWidth: '700px' }}>
        <div className="logo-wrapper mb-4">
          <img src={brandLogo} alt="Logo" className="img-fluid" style={{ height: '80px' }} />
        </div>

        <div className="heading-wrapper mb-4">
          <div className="fs-2 fw-bold text-white lh-sm mb-2">Download Yoospeak Now</div>
          <div className="text-base text-white">Learn • Connect • Speak Globally</div>
        </div>

        <Row className="g-2 g-md-3 mb-4">
          {stats.map((stat, index) => (
            <Col key={index} xs={index === stats.length - 1 ? 12 : 6} sm={4}>
              <Card className="h-100 border-0 rounded-3 shadow">
                <Card.Body className="text-center p-3">
                  <div className="d-flex align-items-center justify-content-center gap-2 text-sm mb-1">
                    <i className={`bi ${stat.icon} text-primary text-base`}></i>
                    <span className="text-gray-600">{stat.label}</span>
                  </div>
                  <div className="fw-bold text-blue text-xl">{stat.value}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <div className="bg-blue border border-info border-opacity-75 d-flex align-items-center justify-content-center gap-2 fw-medium rounded-pill text-lg text-white mb-4"
          style={{
            background: 'linear-gradient(135deg, rgba(12, 17, 76, 0.68), rgba(28, 40, 178, 0.68))',
            padding: "0.75rem 1.5rem"
          }}
        >
          <Button variant="primary" className="btn-custom" onClick={handleOpenApp}>Open in App</Button>
          {/* <i className="bi bi-link-45deg fs-4 lh-1"></i> */}
          {/* <span className="link">www.yoospeak.com/app</span> */}
        </div>

        {/* <div className="btn-wrapper mb-3">
          <Button variant="primary" className="btn-custom" onClick={handleOpenApp}>Open in App</Button>
        </div> */}

        <div className="download-app-wrapper">
          <div className="text-xs text-light mb-3">Don't have the app? Get it from:</div>
          <div className="app-btn-wrapper d-flex flex-wrap justify-content-center gap-3">
            <Link to={'https://play.google.com/store/apps/details?id=com.yoospeak.learning'} target="_blank" rel="noopener noreferrer">
              <img src={require('../assets/images/play-store.png')} className="img-fluid rounded-3" alt="Google Play Store" style={{ width: "155px" }} />
            </Link>
            <Link to={'https://apps.apple.com/in/app/yoospeak/id6746839854'} target="_blank" rel="noopener noreferrer">
              <img src={require('../assets/images/app-store.png')} className="img-fluid rounded-3" alt="App Store" style={{ width: "155px" }} />
            </Link>
          </div>
          <div className="text-sm text-light fst-italic mt-3">Join thousands learning languages worldwide</div>
        </div>
      </div>
    </div>
  );
};
